import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileOutput, Upload, Download, AlertTriangle, CheckCircle2, Info, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { API } from "@/context/AuthContext";
import { TallyHistory, downloadJob } from "@/components/TallyHistory";
import { TallyMappings } from "@/components/TallyMappings";
import { TallyUnmapped } from "@/components/TallyUnmapped";

const MAX_DAYS = 92;
const firstOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const todayISO = () => new Date().toISOString().slice(0, 10);
const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);

const FilePick = ({ id, label, hint, multiple, file, onChange, required }) => (
  <div className="space-y-1">
    <Label htmlFor={id} className="text-xs font-semibold text-slate-700">{label}{required && <span className="text-red-500"> *</span>}</Label>
    <label htmlFor={id} className="flex items-center gap-2 rounded-md border border-dashed border-slate-300 bg-white px-3 py-2 text-xs text-slate-600 cursor-pointer hover:border-sky-500 hover:bg-sky-50/40 transition-colors" data-testid={`${id}-label`}>
      <Upload className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      <span className="truncate">{file ? (Array.isArray(file) ? file.map((f) => f.name).join(", ") : file.name) : hint}</span>
    </label>
    <input id={id} type="file" accept=".xlsx,.xls,.csv" multiple={multiple} className="sr-only" data-testid={id}
      onChange={(e) => onChange(multiple ? Array.from(e.target.files || []) : e.target.files?.[0] || null)} />
  </div>
);

const SummaryBox = ({ s }) => {
  const files = Object.entries(s).filter(([k]) => !["warnings", "errors", "log", "source", "unmapped"].includes(k));
  return (
    <Card className="border-emerald-200 bg-emerald-50/40" data-testid="tally-summary">
      <CardHeader className="pb-2"><CardTitle className="text-base font-semibold text-emerald-900 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Export downloaded</CardTitle>
        <p className="text-xs text-emerald-800">Source: {s.source === "sap-api" ? "SAP APIs (live)" : "Fiori exports (uploaded)"} · {s.warnings} warnings · {s.errors} errors — full detail in Export_Log_*.csv inside the zip</p></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
          {files.map(([k, n]) => <div key={k} className="rounded border border-emerald-200 bg-white px-3 py-2" data-testid={`tally-summary-${k}`}><p className="text-[10px] uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</p><p className="text-lg font-black text-slate-900">{n}</p><p className="text-[10px] text-slate-400">rows</p></div>)}
        </div>
        {s.log?.length > 0 && (
          <div className="max-h-56 overflow-y-auto rounded border border-amber-200 bg-amber-50/60 p-2 text-xs" data-testid="tally-summary-log">
            {s.log.map((l, i) => <p key={i} className={l.Level === "ERROR" ? "text-red-700" : "text-amber-800"}><span className="font-mono font-semibold">{l.Level}</span> · {l.Voucher}{l["SAP Document"] ? ` · ${l["SAP Document"]}` : ""} — {l.Message}</p>)}
            {(s.warnings + s.errors) > s.log.length && <p className="text-slate-500 italic mt-1">…{s.warnings + s.errors - s.log.length} more in the log file</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function TallyExport() {
  const [types, setTypes] = useState([]);
  const [info, setInfo] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayISO());
  const [manual, setManual] = useState(false);
  const [posting, setPosting] = useState(null);
  const [clearing, setClearing] = useState(null);
  const [wht, setWht] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState("");
  const [histKey, setHistKey] = useState(0);
  const [unmapped, setUnmapped] = useState(null);
  const [unmappedLabel, setUnmappedLabel] = useState("");
  const [queue, setQueue] = useState(null);
  const pushRows = (items) => { setQueue({ seq: Date.now(), items }); toast.success(`${items.length} row${items.length > 1 ? "s" : ""} added to the mappings editor — fill the Tally name and Save`); };
  const editRow = (u) => setQueue({ seq: Date.now(), items: [{ sheet: u.sheet, key: u.key, row: null }] });
  const showUnmapped = async (j) => {
    try { const r = await axios.get(`${API}/tally-export/jobs/${j.id}`); setUnmapped(r.data.unmapped || []); setUnmappedLabel(`Export ${j.from_date} → ${j.to_date}`); } catch { toast.error("Could not load that export"); }
  };

  useEffect(() => {
    axios.get(`${API}/tally-export/voucher-types`).then((r) => { setTypes(r.data); setChosen(r.data.map((t) => t.key)); }).catch(() => toast.error("Could not load voucher types"));
    axios.get(`${API}/tally-export/info`).then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const span = daysBetween(from, to);
  const rangeBad = !from || !to || span < 0 || span > MAX_DAYS;
  const canRun = !busy && chosen.length > 0 && !rangeBad && (!manual || posting);

  const run = async () => {
    setBusy(true); setErr(""); setSummary(null); setProgress("Starting export job…");
    try {
      let start;
      if (manual) {
        const fd = new FormData();
        fd.append("from_date", from); fd.append("to_date", to); fd.append("vouchers", chosen.join(","));
        fd.append("line_item_files", posting);
        if (clearing) fd.append("line_item_files", clearing);
        if (wht) fd.append("wht_file", wht);
        start = await axios.post(`${API}/tally-export/manual`, fd);
      } else {
        start = await axios.post(`${API}/tally-export`, { from_date: from, to_date: to, vouchers: chosen });
      }
      const jobId = start.data.job_id;
      const t0 = Date.now();
      let job = start.data;
      while (job.status === "running") {
        await new Promise((r) => setTimeout(r, 2500));
        job = (await axios.get(`${API}/tally-export/jobs/${jobId}`)).data;
        setProgress(`${job.stage || "Extracting from SAP…"} ${Math.round((Date.now() - t0) / 1000)}s`);
      }
      if (job.status === "error") throw new Error(job.error || "Export failed");
      await downloadJob(jobId, `Tally_Export_${from.replace(/-/g, "")}_${to.replace(/-/g, "")}.zip`);
      setSummary(job.summary || {});
      setUnmapped(job.unmapped || []);
      setUnmappedLabel(`Export ${from} → ${to}`);
      setHistKey((k) => k + 1);
      toast.success("Tally export downloaded");
    } catch (e) {
      let msg = e.response?.data?.detail || e.message || "Export failed";
      if (e.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* keep */ } }
      setErr(msg); toast.error("Export failed");
    } finally { setBusy(false); setProgress(""); }
  };

  return (
    <div className="space-y-6" data-testid="tally-export-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><FileOutput className="h-7 w-7 text-sky-700" /> Tally Export (SAP → Tally)</h1>
          <p className="text-sm text-slate-500 mt-1">Pulls vouchers for a date range from SAP S/4HANA Public Cloud{info?.company_code ? ` (company code ${info.company_code})` : ""} and fills the five Tally upload templates — Purchase, Journal (FB60), Sales / DN / CN, Receipts and Payments — plus an export log, as one zip.</p>
        </div>
        {info?.tenant_url && <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600" data-testid="tally-tenant">{info.tenant_url.replace("https://", "")} · {info.status || "—"}</span>}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="xl:col-span-2 border-slate-200 shadow-sm" data-testid="tally-form">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold text-slate-900">Run an export</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1"><Label htmlFor="tally-from" className="text-xs font-semibold text-slate-700">From date</Label><input id="tally-from" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="tally-from" /></div>
              <div className="space-y-1"><Label htmlFor="tally-to" className="text-xs font-semibold text-slate-700">To date</Label><input id="tally-to" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="tally-to" /></div>
              <div className="flex items-end"><p className={`text-xs ${rangeBad ? "text-red-600 font-semibold" : "text-slate-500"}`} data-testid="tally-range-hint">{span < 0 ? "From date is after To date" : span > MAX_DAYS ? `Max ${MAX_DAYS} days (one quarter) per run — currently ${span}` : `${span + 1} day${span ? "s" : ""} selected`}</p></div>
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-700 mb-2">Voucher types</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="tally-voucher-types">
                {types.map((t) => (
                  <label key={t.key} htmlFor={`tally-v-${t.key}`} className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer transition-colors ${chosen.includes(t.key) ? "border-sky-300 bg-sky-50/60" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                    <Checkbox id={`tally-v-${t.key}`} checked={chosen.includes(t.key)} data-testid={`tally-voucher-${t.key}`}
                      onCheckedChange={(c) => setChosen((cur) => c ? [...cur, t.key] : cur.filter((k) => k !== t.key))} />
                    <span className="text-slate-800">{t.label}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-3 mt-1.5 text-xs"><button type="button" className="text-sky-700 hover:underline" onClick={() => setChosen(types.map((t) => t.key))} data-testid="tally-select-all">Select all</button><button type="button" className="text-slate-500 hover:underline" onClick={() => setChosen([])} data-testid="tally-select-none">Clear</button></div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Label htmlFor="tally-manual" className="text-sm font-semibold text-slate-800">Use Fiori exports for journal entries</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Off (default): journal entries, GST split, TDS and payment clearing are read live from the standard journal-entry API (SAP_COM_0303). Turn on to upload “Display Line Items in General Ledger” exports instead — e.g. to re-run a backlog month exactly as it was exported.</p>
                </div>
                <Switch id="tally-manual" checked={manual} onCheckedChange={setManual} data-testid="tally-manual-toggle" />
              </div>
              {manual && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1" data-testid="tally-manual-files">
                  <FilePick id="tally-file-posting" label="Posting-date export" hint="Ledger 0L · Posting Date = period · all items" file={posting} onChange={setPosting} required />
                  <FilePick id="tally-file-clearing" label="Clearing-date export" hint="Clearing Date = period · recon accounts · cleared items" file={clearing} onChange={setClearing} />
                  <FilePick id="tally-file-wht" label="Withholding tax export (optional)" hint="TDS code / base / amount per journal entry" file={wht} onChange={setWht} />
                </div>
              )}
            </div>

            {err && <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="tally-error"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /><span className="break-words">{err}</span></div>}

            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={run} disabled={!canRun} className="bg-sky-700 hover:bg-sky-800 text-white" data-testid="tally-export-btn">
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {progress || "Extracting from SAP…"}</> : <><Download className="h-4 w-4 mr-2" /> Export to Tally templates</>}
              </Button>
              {busy && <p className="text-xs text-slate-500" data-testid="tally-busy-hint">Runs in the background on the server — a full month across five APIs takes 1–3 minutes. The zip downloads automatically when done.</p>}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm" data-testid="tally-info">
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2"><Info className="h-4 w-4 text-sky-700" /> Where each template comes from</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-xs text-slate-600">
            {(info?.sources || []).map((s) => <div key={s.voucher} className="rounded border border-slate-200 bg-white p-2.5"><p className="font-semibold text-slate-800">{s.voucher}</p><p className="font-mono text-[11px] text-slate-600 mt-0.5 break-words">{s.api}</p><p className="text-[11px] text-slate-400 mt-0.5">{s.scenario}</p></div>)}
            {info?.interim_note && <p className="rounded border border-sky-200 bg-sky-50/60 p-2.5 text-sky-900">{info.interim_note}</p>}
            <ul className="list-disc pl-4 space-y-1 text-[11px] text-slate-500">
              <li>Multi-line vouchers repeat header fields on every row; amounts (taxable, GST, totals, TDS) sit on the first row only.</li>
              <li>Reversed documents, cancelled billing documents and parked invoices are skipped and listed in the log.</li>
              <li>Ledger names default to SAP names until <span className="font-mono">mappings.xlsx</span> is filled by the accounts team.</li>
              <li>Import into a test company in Tally first and reconcile totals against SAP before posting to live books.</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {summary && <SummaryBox s={summary} />}
      {unmapped && <TallyUnmapped items={unmapped} label={unmappedLabel} onAdd={pushRows} onEdit={editRow} />}
      <TallyHistory refreshKey={histKey} onShowUnmapped={showUnmapped} />
      <TallyMappings queue={queue} />
    </div>
  );
}
